/**
 * The countries a monitored prompt can actually be asked from.
 *
 * This list is not cosmetic and must not be extended freely. The backend turns a country code
 * into a DataForSEO location via `DataForSeoService.CountryToLocationCode`, whose final case is
 * `_ => 2840`, the United States — so a country it does not know is not an error, it is a US
 * measurement wearing another country's label. The picker previously offered 36 countries where
 * 20 were honoured, which is how "we monitor Saudi Arabia" and "we monitor the US" became the
 * same request.
 *
 * Adding a market means adding it to that switch first, then here.
 */
export const TARGET_COUNTRIES = [
  { value: "US", label: "United States" },
  { value: "GB", label: "United Kingdom" },
  { value: "DE", label: "Germany" },
  { value: "AT", label: "Austria" },
  { value: "CH", label: "Switzerland" },
  { value: "FR", label: "France" },
  { value: "ES", label: "Spain" },
  { value: "IT", label: "Italy" },
  { value: "NL", label: "Netherlands" },
  { value: "SE", label: "Sweden" },
  { value: "NO", label: "Norway" },
  { value: "DK", label: "Denmark" },
  { value: "FI", label: "Finland" },
  { value: "CA", label: "Canada" },
  { value: "MX", label: "Mexico" },
  { value: "BR", label: "Brazil" },
  { value: "AU", label: "Australia" },
  { value: "JP", label: "Japan" },
  { value: "KR", label: "South Korea" },
  { value: "IN", label: "India" },
  { value: "PT", label: "Portugal" },
];

/** Languages we research and write in. Codes match TargetLocale.LanguageName. */
export const TARGET_LANGUAGES = [
  { value: "en", label: "English" },
  { value: "de", label: "German" },
  { value: "fr", label: "French" },
  { value: "es", label: "Spanish" },
  { value: "it", label: "Italian" },
  { value: "nl", label: "Dutch" },
  { value: "pt", label: "Portuguese" },
  { value: "sv", label: "Swedish" },
  { value: "no", label: "Norwegian" },
  { value: "da", label: "Danish" },
  { value: "fi", label: "Finnish" },
  { value: "pl", label: "Polish" },
  { value: "cs", label: "Czech" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
  { value: "zh", label: "Chinese" },
];

export const languageLabel = (code) =>
  TARGET_LANGUAGES.find((l) => l.value === code)?.label ?? code;

/** "Portugal · Portuguese", or whichever half is set. */
export function formatMarket(country, language) {
  const c = country ? countryLabel(country) : null;
  const l = language ? languageLabel(language) : null;
  if (c && l) return `${c} · ${l}`;
  return c || l || null;
}

/** The country's English name, or the code itself when it is not one we can target. */
export const countryLabel = (code) =>
  TARGET_COUNTRIES.find((c) => c.value === code)?.label ?? code;

export const countryCodeToFlagEmoji = (code) =>
  (code ?? "")
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));
